import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ALL_GRANTABLE_PERMISSIONS } from './permissions.js';
const c = initContract();
/**
 * Role management (PART 16.4.4, the Roles & Permissions screen's API).
 *
 * Shapes only — the rules live in RolesService and the guard:
 *  - every route here requires an `admin`-area permission (BR-PERM-05);
 *  - shipped roles refuse edit/deactivate at the API AND at the DB trigger;
 *  - Administrator is assigned/revoked by the Owner alone (BR-PERM-72);
 *  - there is deliberately NO delete route: custom roles are retired by
 *    deactivation (BR-PERM-59/60), and the composite RESTRICT FK makes an
 *    in-use role undeletable even by SQL.
 *
 * Note what is absent, as everywhere: a tenant id. The tenant comes from the
 * session alone (§5.7); a role id from another tenant is simply not found.
 */
/** A permission string, validated against the closed vocabulary — the 60
 *  grid entries plus the named extensions (R10's bounced-flag override). */
export const PermissionSchema = z.enum(ALL_GRANTABLE_PERMISSIONS);
const MessageSchema = z.object({ message: z.string() }).strict();
export const SecurityRoleSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    /** True on the three shipped locked roles; their grids are read-only. */
    system: z.boolean(),
    active: z.boolean(),
    permissions: PermissionSchema.array(),
    /** How many users currently hold the role — drives BR-PERM-59's message. */
    holders: z.number().int().nonnegative(),
})
    .strict();
const SecurityRoleNameSchema = z.string().trim().min(1).max(80);
const RoleDescriptionSchema = z.string().trim().max(500);
export const CreateRoleRequestSchema = z
    .object({
    name: SecurityRoleNameSchema,
    description: RoleDescriptionSchema.optional(),
    /** The grid — any subset of the 60. An empty grid is a legal start (16.4.4). */
    permissions: PermissionSchema.array(),
})
    .strict();
export const UpdateRoleRequestSchema = z
    .object({
    name: SecurityRoleNameSchema.optional(),
    description: RoleDescriptionSchema.optional(),
    /** Replaces the whole grid when present — changes apply at once (16.0). */
    permissions: PermissionSchema.array().optional(),
})
    .strict();
export const DuplicateRoleRequestSchema = z
    .object({
    /** Defaults to `<original> (copy)` per BR-PERM-71. */
    name: SecurityRoleNameSchema.optional(),
})
    .strict();
export const AssignRoleRequestSchema = z
    .object({
    /** null removes the role, leaving the user role-less (rung 4). */
    roleId: z.uuid().nullable(),
})
    .strict();
const roleParams = z.object({ roleId: z.uuid() }).strict();
export const rolesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/roles',
        responses: {
            200: z.object({ roles: SecurityRoleSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'All roles of the caller’s company, shipped and custom, with their grids.',
    },
    create: {
        method: 'POST',
        path: '/api/roles',
        body: CreateRoleRequestSchema,
        responses: {
            201: SecurityRoleSchema,
            /** BR-PERM-01 — Create/Edit/Delete/Approve in an area whose View is off. */
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Create a custom role from an explicit grid (BR-PERM-54; BR-PERM-64 bounds it).',
    },
    update: {
        method: 'PATCH',
        path: '/api/roles/:roleId',
        pathParams: roleParams,
        body: UpdateRoleRequestSchema,
        responses: {
            200: SecurityRoleSchema,
            /** BR-PERM-01 — see create. An edit that clears View is refused too. */
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a CUSTOM role’s name/description/grid. A shipped role answers 403 (BR-PERM-70).',
    },
    duplicate: {
        method: 'POST',
        path: '/api/roles/:roleId/duplicate',
        pathParams: roleParams,
        body: DuplicateRoleRequestSchema,
        responses: {
            201: SecurityRoleSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'One-click Duplicate: an editable custom copy of any role — the locked-role customization path (BR-PERM-71).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/roles/:roleId/deactivate',
        pathParams: roleParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: SecurityRoleSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Retire a custom role. Refused while anybody holds it (BR-PERM-59); shipped roles answer 403.',
    },
    reactivate: {
        method: 'POST',
        path: '/api/roles/:roleId/reactivate',
        pathParams: roleParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: SecurityRoleSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Bring a deactivated custom role back (BR-PERM-60).',
    },
    assign: {
        method: 'PUT',
        path: '/api/users/:userId/role',
        pathParams: z.object({ userId: z.uuid() }).strict(),
        body: AssignRoleRequestSchema,
        responses: {
            200: z
                .object({ userId: z.uuid(), roleId: z.uuid().nullable(), roleName: z.string().nullable() })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Assign a role to a user, or null to leave them role-less. Administrator moves only by the Owner’s hand (BR-PERM-72).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=roles.js.map